import type {
  ContentDashboard,
  ContentLevel,
  ContentStatus,
  ContentTheme,
  ContentTopic,
} from './types.js'

export interface TopicLocation {
  level: ContentLevel
  theme: ContentTheme
  topic: ContentTopic
}

export interface TopicReviewSummary {
  aboveBandCount: number
  aboveBandNeedsAttention: boolean
  aboveBandCap: number | null
  durationMatches: boolean
  duplicateSummary: string
  findings: string[]
  missionMatches: boolean
  needsReview: boolean
  quotaCount: number
}

export interface ContentTopicParentDestination {
  kind: 'coverage' | 'curriculum' | 'vocabulary'
  label: 'Scenarios' | 'Coverage' | 'Vocabulary'
  to: string
}

export function topicCount(level: ContentLevel): number {
  return level.themes.reduce((total, theme) => total + theme.topics.length, 0)
}

export function targetCount(level: ContentLevel): number {
  return level.themes.reduce(
    (total, theme) =>
      total + theme.topics.reduce((sum, topic) => sum + topic.targetVocab.length, 0),
    0,
  )
}

export function labelForStatus(status: ContentStatus): string {
  if (status === 'gated') return 'Release gated'
  if (status === 'planned') return 'Planned'
  return 'Existing'
}

export function badgeVariant(
  status: ContentStatus,
): 'danger' | 'muted' | 'neutral' {
  if (status === 'gated') return 'danger'
  if (status === 'planned') return 'muted'
  return 'neutral'
}

export function aboveBandPolicy(level: ContentLevel): string {
  const constraints: string[] = []
  if (level.band.aboveBandPerTopic !== null) {
    constraints.push(
      `at most ${level.band.aboveBandPerTopic} above-band target${level.band.aboveBandPerTopic === 1 ? '' : 's'} per topic`,
    )
  }
  if (level.band.aboveBandLevelPercent !== null) {
    constraints.push(
      `at most ${level.band.aboveBandLevelPercent}% above band across the level`,
    )
  }
  if (constraints.length > 0) return constraints.join(' and ')
  if (level.level === 'B2') return 'C1+ targets require approved exceptions'
  return 'no numeric above-band cap recorded'
}

export function allTopics(dashboard: Pick<ContentDashboard, 'levels'>): TopicLocation[] {
  return dashboard.levels.flatMap((level) =>
    level.themes.flatMap((theme) =>
      theme.topics.map((topic) => ({ level, theme, topic })),
    ),
  )
}

export function topicReviewSummary(
  level: ContentLevel,
  topic: ContentTopic,
  priorTargetLemmas: Iterable<string>,
): TopicReviewSummary {
  const validVocab = topic.targetVocab.filter((item) => item.bandStatus !== 'invalid')
  const invalidCount = topic.targetVocab.length - validVocab.length
  const missionMatches =
    topic.taskShape.missionCount === topic.taskShape.expectedMissionCount
  const aboveBandCount = validVocab.filter((item) => item.aboveBand).length
  const priorProgramTargetLemmas = new Set(priorTargetLemmas)
  const uniqueTargetLemmas = new Set(
    validVocab.map((item) => item.canonicalLemma),
  )
  const quotaCount = new Set(
    validVocab
      .filter((item) => item.bandStatus === 'in_window' || item.waived)
      .map((item) => item.canonicalLemma),
  ).size
  const hasWithinTopicDuplicates =
    uniqueTargetLemmas.size !== validVocab.length
  const reusesPriorProgramTarget = validVocab.some((item) =>
    priorProgramTargetLemmas.has(item.canonicalLemma),
  )
  const hasUnwaivedAboveBand = validVocab.some(
    (item) => item.aboveBand && !item.waived,
  )
  const hasOtherUnwaivedOutOfWindow = validVocab.some(
    (item) => item.bandStatus !== 'in_window' && !item.aboveBand && !item.waived,
  )
  const aboveBandCap = level.band.aboveBandPerTopic
  const exceedsAboveBandCap =
    aboveBandCap !== null && aboveBandCount > aboveBandCap
  const aboveBandNeedsAttention = hasUnwaivedAboveBand || exceedsAboveBandCap
  const durationMatches =
    topic.taskShape.targetDurationSec === level.band.targetDurationSec
  const advisoryOnlySentenceCount = Math.max(
    0,
    topic.sentenceStats.aboveAdvisoryCount -
      topic.sentenceStats.aboveHardCount,
  )
  const findings: string[] = []
  if (invalidCount > 0) findings.push('Invalid vocabulary entries; correct the source content')

  if (
    topic.sentenceStats.unbalancedQuotes ||
    topic.sentenceStats.spansCrossingBlankLine > 0
  ) {
    findings.push('Malformed quote pairing; sentence stats are unreliable')
  }
  if (topic.sentenceStats.aboveHardCount > 0) {
    findings.push(
      `${topic.sentenceStats.aboveHardCount} sentence${topic.sentenceStats.aboveHardCount === 1 ? '' : 's'} over ${level.level} ${level.band.hardSentenceWords}-word maximum`,
    )
  }
  if (advisoryOnlySentenceCount > 0) {
    findings.push(
      `${advisoryOnlySentenceCount} sentence${advisoryOnlySentenceCount === 1 ? '' : 's'} above ${level.level} ${level.band.advisorySentenceWords}-word guidance`,
    )
  }
  if (hasUnwaivedAboveBand) {
    findings.push('Above-band target needs an approved exception')
  }
  if (exceedsAboveBandCap) {
    findings.push('Above-band target count exceeds the lesson cap')
  }
  if (hasOtherUnwaivedOutOfWindow) {
    findings.push('Out-of-window target needs an approved exception')
  }
  if (hasWithinTopicDuplicates) findings.push('Duplicate target inside this topic')
  else if (reusesPriorProgramTarget) {
    findings.push('Reuses an earlier program target')
  }
  if (quotaCount < level.band.inWindowQuota) {
    findings.push(
      `Only ${quotaCount} of ${level.band.inWindowQuota} quota-counting targets`,
    )
  }
  if (!missionMatches) findings.push('Mission count differs from the level contract')
  if (!durationMatches) findings.push('Duration differs from the level contract')

  return {
    aboveBandCount,
    aboveBandNeedsAttention,
    aboveBandCap,
    durationMatches,
    duplicateSummary: hasWithinTopicDuplicates
      ? 'within-lesson duplicates detected'
      : reusesPriorProgramTarget
        ? 'reuses an earlier program target'
        : 'no duplicate targets',
    findings,
    missionMatches,
    needsReview: findings.length > 0,
    quotaCount,
  }
}

export function topicHref(
  location: TopicLocation,
  returnTo?: string,
  from?: 'coverage' | 'vocabulary',
  fromLevel?: string,
): string {
  const context = new URLSearchParams({
    level: location.level.programLevelId,
    theme: location.theme.id,
  })
  if (returnTo !== undefined) context.set('returnTo', returnTo)
  if (from) context.set('from', from)
  if (fromLevel) context.set('fromLevel', fromLevel)
  return `/admin/content/scenarios/${encodeURIComponent(location.topic.id)}?${context.toString()}`
}

export function curriculumReturnContext(searchParams: URLSearchParams): string {
  const context = new URLSearchParams()
  const allowed = new Set(['q', 'program', 'level', 'theme', 'kind', 'status', 'availability', 'review', 'page', 'sort'])
  for (const [key, value] of searchParams) {
    if (allowed.has(key) && value && (key === 'sort' || !context.has(key))) context.append(key, value)
  }
  return context.toString()
}

export function vocabularyReturnContext(searchParams: URLSearchParams): string {
  const context = new URLSearchParams()
  for (const key of [
    'level',
    'view',
    'q',
    'grade',
    'band',
    'exception',
    'theme',
    'reuse',
  ]) {
    const value = searchParams.get(key)
    if (value) context.set(key, value)
  }
  return context.toString()
}

export function contentTopicParentDestination(
  searchParams: URLSearchParams,
  canonicalFallback?: { level: string; theme: string },
): ContentTopicParentDestination {
  const fromLevel = searchParams.get('fromLevel') ?? ''
  const knownLevel = /^[a-zA-Z0-9-]+$/.test(fromLevel)
  if (searchParams.get('from') === 'vocabulary' && knownLevel) {
    const encodedReturnContext = searchParams.get('returnTo')
    const returnContext = vocabularyReturnContext(
      new URLSearchParams(encodedReturnContext ?? `level=${fromLevel}`),
    )
    return {
      kind: 'vocabulary',
      label: 'Vocabulary',
      to: `/admin/content/vocabulary?${returnContext}`,
    }
  }
  if (
    searchParams.get('from') === 'coverage' &&
    knownLevel
  ) {
    return {
      kind: 'coverage',
      label: 'Coverage',
      to: `/admin/content/coverage?${new URLSearchParams({ level: fromLevel }).toString()}`,
    }
  }

  const encodedReturnContext = searchParams.get('returnTo')
  const returnContext = curriculumReturnContext(
    encodedReturnContext === null
      ? canonicalFallback === undefined
        ? searchParams
        : new URLSearchParams(canonicalFallback)
      : new URLSearchParams(encodedReturnContext),
  )
  return {
    kind: 'curriculum',
    label: 'Scenarios',
    to: `/admin/content/scenarios${returnContext ? `?${returnContext}` : ''}`,
  }
}

export function guidelinesApplyToLevel(
  levelRange: string,
  level: string,
): boolean {
  const ranks = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
  const [start, end = start] = levelRange.split('-')
  const levelRank = ranks.indexOf(level)
  const startRank = ranks.indexOf(start ?? '')
  const endRank = ranks.indexOf(end ?? '')
  return (
    levelRank >= startRank &&
    levelRank <= endRank &&
    startRank >= 0 &&
    endRank >= 0
  )
}
